/**
 * Simulated GET /api/qb-storage-billed — the "Billed (QB)" feed behind
 * Warehouse → Expected Revenue.
 *
 * The response keeps `QbStorageBilledStatus` from src/lib/api/qbStorageBilled.ts
 * exactly (ok / last_updated / counts / qb_configured) and adds `byJob` and
 * `byMonth` alongside it, which the UI ignores and the network tab shows.
 *
 * Derived from the snapshot: `warehouse_assignments` — one occupied vault per
 * row, with the real job number, real customer, and the real `date_entered` /
 * `billed_until` window where present. Storage bills at the owner's constant
 * **$192 per vault per month**, invoiced as item 160 "Contents - Storage".
 * Counts also fold in the real `qb_storage_billed` rows so `backdated` vs
 * `qb_sync` matches what the table actually holds.
 */
import { loadTable } from '~/demo/data';
import { once } from './prng';
import { nyDate, num, round2, str } from './finance';

/** Owner's spec constants. */
import { VAULT_RATE } from '~/components/dashboards/warehouse/constants';
/** Single definition lives in warehouse/constants.ts. */
export const STORAGE_RATE_PER_VAULT_MONTH = VAULT_RATE;
export const STORAGE_ITEM_REF = '160';
export const STORAGE_ITEM_NAME = 'Contents - Storage';

const MONTH_MS = 30.4375 * 86_400_000;

export const simulatedStorageBilled = once(async () => {
  const [assignments, billed] = await Promise.all([
    loadTable('warehouse_assignments'), loadTable('qb_storage_billed'),
  ]);

  const now = new Date();
  const occupied = assignments.filter((a) => str(a.job_number) && !str(a.placeholder_kind));

  const byJobMap = new Map<string, {
    job_number: string; customer: string; vaults: number;
    start: string; end: string; months: number; amount: number;
  }>();

  for (const a of occupied) {
    const job = str(a.job_number);
    const start = str(a.date_entered) || str(a.created_at);
    const end = str(a.billed_until) || nyDate(now);
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    const months = Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
      ? Math.max(1, Math.round((endMs - startMs) / MONTH_MS))
      : 1;
    const cur = byJobMap.get(job) ?? {
      job_number: job, customer: str(a.customer), vaults: 0,
      start: start.slice(0, 10), end: end.slice(0, 10), months, amount: 0,
    };
    cur.vaults += 1;
    cur.months = Math.max(cur.months, months);
    if (start.slice(0, 10) < cur.start) cur.start = start.slice(0, 10);
    if (end.slice(0, 10) > cur.end) cur.end = end.slice(0, 10);
    byJobMap.set(job, cur);
  }

  const byJob = [...byJobMap.values()].map((j) => ({
    ...j,
    rate_per_vault_month: STORAGE_RATE_PER_VAULT_MONTH,
    item_ref: STORAGE_ITEM_REF,
    item_name: STORAGE_ITEM_NAME,
    amount: round2(j.vaults * j.months * STORAGE_RATE_PER_VAULT_MONTH),
  })).sort((a, b) => b.amount - a.amount);

  // Monthly run-rate: every vault occupied in a month bills that month.
  const byMonthMap = new Map<string, { vaults: number; amount: number }>();
  for (const j of byJob) {
    const start = new Date(`${j.start.slice(0, 7)}-01T00:00:00`);
    for (let i = 0; i < j.months && i < 36; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const cur = byMonthMap.get(key) ?? { vaults: 0, amount: 0 };
      cur.vaults += j.vaults;
      cur.amount = round2(cur.amount + j.vaults * STORAGE_RATE_PER_VAULT_MONTH);
      byMonthMap.set(key, cur);
    }
  }
  const byMonth = [...byMonthMap.entries()]
    .map(([month, v]) => ({ month, ...v }))
    .sort((a, b) => a.month.localeCompare(b.month));

  const lastUpdated = billed
    .map((r) => str(r.updated_at))
    .filter(Boolean)
    .sort()
    .pop() ?? now.toISOString();

  return {
    ok: true,
    qb_configured: true,
    simulated: true,
    last_updated: lastUpdated,
    counts: {
      total: billed.length + byJob.length,
      backdated: billed.filter((r) => str(r.source) === 'backdated').length,
      qb_sync: billed.filter((r) => str(r.source) === 'qb_sync').length + byJob.length,
    },
    demo_note:
      `byJob/byMonth are derived from warehouse_assignments at $${STORAGE_RATE_PER_VAULT_MONTH} per vault per month `
      + `(item ${STORAGE_ITEM_REF} "${STORAGE_ITEM_NAME}"); counts also include the real qb_storage_billed rows.`,
    byJob,
    byMonth,
    total_billed: round2(byJob.reduce((s, j) => s + j.amount, 0)),
    /** Kept so the sum is visibly reconcilable against the real table. */
    table_billed: round2(billed.reduce((s, r) => s + num(r.billed_amount), 0)),
  };
});

export async function simulatedStorageBilledSync() {
  const status = await simulatedStorageBilled();
  return {
    ok: true,
    simulated: true,
    synced: status.byJob.length,
    last_updated: status.last_updated,
    demo_note: 'Sync is a no-op in the demo; storage billing is recomputed from warehouse_assignments.',
  };
}
